import { useState, useEffect } from "react";
import { api } from "../api";
import type { AssetFullDetail, FieldDef, Identity } from "../types";
import Icon from "../components/Icon";
import Badge from "../components/Badge";
import ChipEditor from "./detail/ChipEditor";
import SpecsEditor from "./detail/SpecsEditor";
import RelationsEditor from "./detail/RelationsEditor";
import MediaEditor from "./detail/MediaEditor";
import PreviewPane from "./detail/PreviewPane";
import ClientAssetView from "./detail/ClientAssetView";
import type { ClientRow } from "../types";

type AssetDetailProps = {
  clientId: string;
  clientRow: ClientRow | null;
  identity: Identity;
  assetId: string; // "new" = create mode
  onBack: () => void;
  onShowToast: (msg: string) => void;
};

function pubStateToBadge(s: string | null): "published" | "draft" | "archived" {
  if (s === "published") return "published";
  if (s === "archived") return "archived";
  return "draft";
}

function getOptions(fd: FieldDef): string[] {
  if (!fd.options) return [];
  try {
    return JSON.parse(fd.options) as string[];
  } catch {
    return [];
  }
}

const FIELD_LABEL: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  marginBottom: 6,
  fontWeight: 500,
};

function FieldInput({
  fd,
  value,
  onChange,
}: {
  fd: FieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  switch (fd.field_type) {
    case "textarea":
      return (
        <textarea
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          style={{ resize: "vertical" }}
        />
      );
    case "boolean":
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={value === "true" || value === "1"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          />
          <span style={{ fontSize: 14 }}>{fd.display_name}</span>
        </label>
      );
    case "select": {
      const opts = getOptions(fd);
      return (
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    default:
      return (
        <input
          className="input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}


export default function AssetDetail({
  clientId,
  clientRow,
  identity,
  assetId,
  onBack,
  onShowToast,
}: AssetDetailProps) {
  const isNew = assetId === "new";
  const isPlatformOwner = identity.role === "platform_owner";

  if (!isPlatformOwner && !isNew) {
    return (
      <ClientAssetView
        clientId={clientId}
        clientRow={clientRow}
        identity={identity}
        assetId={assetId}
        onBack={onBack}
        onShowToast={onShowToast}
      />
    );
  }
  const [previewWidth, setPreviewWidth] = useState<"desktop" | "mobile">("desktop");

  const [detail, setDetail] = useState<AssetFullDetail | null>(null);
  const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [titleDraft, setTitleDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (isNew) {
      api
        .getFieldDefs(clientId)
        .then((res) => {
          setFieldDefs(res.field_defs);
          setFields({});
          setTitleDraft("");
        })
        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    } else {
      Promise.all([api.getFieldDefs(clientId), api.getAsset(assetId)])
        .then(([fdRes, det]) => {
          setFieldDefs(fdRes.field_defs);
          setDetail(det);
          setFields(det.fields ?? {});
          setTitleDraft(det.asset.title);
        })
        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    }
  }, [clientId, assetId, isNew]);

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.deleteAsset(assetId);
      onShowToast("Asset deleted.");
      onBack();
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : String(e));
      setDeleting(false);
      setDeleteConfirm(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      await api.publishAsset(assetId);
      setDetail((prev) =>
        prev ? { ...prev, asset: { ...prev.asset, publication_state: "published" } } : prev
      );
      onShowToast("Published.");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === "invalid_transition") {
        onShowToast("Already published or cannot publish from current state.");
      } else {
        onShowToast(msg);
      }
    } finally {
      setPublishing(false);
    }
  }

  async function handleSave() {
    setSaveError(null);
    setSaving(true);
    try {
      if (isNew) {
        await api.createAsset(clientId, { title: titleDraft, slug: null, fields });
        onShowToast("Asset created.");
        onBack();
      } else {
        await api.updateAsset(assetId, { fields });
        const fresh = await api.getAsset(assetId);
        setDetail(fresh);
        setFields(fresh.fields ?? {});
        setTitleDraft(fresh.asset.title);
        onShowToast("Saved.");
      }
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const title = isNew ? "New asset" : (detail?.asset.title ?? "…");

  return (
    <div className="page page-enter" style={{ padding: "24px 32px 64px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <button
          onClick={onBack}
          className="btn btn-quiet btn-sm"
          style={{ marginLeft: -6, marginBottom: 20 }}
        >
          <Icon name="arrow_left" size={14} /> Back to catalog
        </button>

        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorBanner message={error} />
        ) : (
          <>
            {/* Page header */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 24,
                marginBottom: 28,
              }}
            >
              <div>
                <span className="eyebrow">{isNew ? "New asset" : "Asset"}</span>
                <h1 className="t-h1" style={{ fontSize: 34, marginTop: 8 }}>
                  {title}
                </h1>
                {!isNew && detail && (
                  <div style={{ marginTop: 10 }}>
                    <Badge kind={pubStateToBadge(detail.asset.publication_state)}>
                      {detail.asset.publication_state ?? "draft"}
                    </Badge>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, paddingTop: 8, flexShrink: 0, alignItems: "center" }}>
                {!isNew && detail?.asset.publication_state === "draft" && (
                  <button
                    className="btn btn-quiet btn-sm"
                    onClick={() => void handlePublish()}
                    disabled={publishing}
                  >
                    <Icon name="upload" size={14} />
                    {publishing ? "Publishing…" : "Publish"}
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => void handleSave()}
                  disabled={saving}
                >
                  {saving ? "Saving…" : isNew ? "Create asset" : "Save changes"}
                </button>
                {!isNew && (
                  deleteConfirm ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>
                        Delete?
                      </span>
                      <button
                        className="btn btn-quiet btn-sm"
                        onClick={() => void handleDelete()}
                        disabled={deleting}
                        style={{ color: "#e58a6d", fontSize: 12 }}
                      >
                        {deleting ? "Deleting…" : "Yes, delete"}
                      </button>
                      <button
                        className="btn btn-quiet btn-sm"
                        onClick={() => setDeleteConfirm(false)}
                        style={{ fontSize: 12 }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-quiet btn-sm"
                      onClick={() => setDeleteConfirm(true)}
                      style={{ color: "var(--fg-subtle)", fontSize: 12 }}
                      title="Soft-delete this asset"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  )
                )}
              </div>
            </div>

            {saveError && (
              <div
                role="alert"
                style={{
                  padding: "12px 16px",
                  background: "rgba(229,138,109,0.10)",
                  border: "1px solid rgba(229,138,109,0.35)",
                  borderRadius: "var(--r-md)",
                  color: "#e58a6d",
                  marginBottom: 20,
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Icon name="x" size={14} />
                {saveError}
              </div>
            )}

            {/* Two-column layout: editors on the left, live preview on the right */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
                gap: 24,
                alignItems: "start",
              }}
            >
              {/* Left column: editable fields */}
              <div>
                {/* Title */}
                <div className="field-group">
                  <div className="field-group-head">
                    <span className="eyebrow">Core</span>
                  </div>
                  <div>
                    <label style={FIELD_LABEL}>
                      Title <span style={{ color: "var(--barn)" }}>*</span>
                    </label>
                    <input
                      className="input"
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      style={{ marginBottom: 0 }}
                    />
                  </div>
                </div>

                {/* Field-def-driven fields */}
                {fieldDefs.length > 0 && (
                  <div className="field-group">
                    <div className="field-group-head">
                      <span className="eyebrow">Fields</span>
                    </div>
                    {fieldDefs.map((fd) => (
                      <div key={fd.field_name} style={{ marginBottom: 14 }}>
                        <label style={FIELD_LABEL}>
                          {fd.display_name}
                          {fd.required === 1 && (
                            <span style={{ color: "var(--barn)" }}> *</span>
                          )}
                        </label>
                        <FieldInput
                          fd={fd}
                          value={fields[fd.field_name] ?? ""}
                          onChange={(v) =>
                            setFields((prev) => ({ ...prev, [fd.field_name]: v }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Specs editor */}
                {!isNew && detail && (
                  <SpecsEditor
                    key={assetId}
                    assetId={assetId}
                    initialSpecs={detail.specs}
                  />
                )}

                {/* Relations editor */}
                {!isNew && detail && (
                  <RelationsEditor
                    key={assetId}
                    assetId={assetId}
                    initialRelations={detail.relations}
                  />
                )}

                {/* Media + tags + claim refs (left column for v2 parity) */}
                {!isNew && detail && (
                  <>
                    <MediaEditor
                      key={assetId}
                      assetId={assetId}
                      initialMedia={detail.media}
                    />
                    <ChipEditor
                      key={`tags-${assetId}`}
                      items={detail.tags.map((t) => t.tag)}
                      label="Tags"
                      placeholder="Add tag…"
                      onAdd={(tag) => api.createTag(assetId, tag).then(() => {})}
                      onRemove={(tag) => api.deleteTag(assetId, tag)}
                    />
                    <ChipEditor
                      key={`claims-${assetId}`}
                      items={detail.claim_refs.map((c) => c.claim_key)}
                      label="Claim refs"
                      placeholder="Add claim key…"
                      onAdd={(key) => api.createClaimRef(assetId, key).then(() => {})}
                      onRemove={(key) => api.deleteClaimRef(assetId, key)}
                    />
                  </>
                )}
              </div>

              {/* Right column: live preview */}
              <PreviewPane
                client={clientRow}
                title={titleDraft || title}
                slug={detail?.asset.slug ?? null}
                fields={fields}
                specs={detail?.specs ?? []}
                media={detail?.media ?? []}
                pubState={detail?.asset.publication_state ?? null}
                previewWidth={previewWidth}
                setPreviewWidth={setPreviewWidth}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div>
      <div className="skeleton" style={{ height: 12, width: 80, marginBottom: 12, borderRadius: 3 }} />
      <div className="skeleton" style={{ height: 36, width: "45%", marginBottom: 28, borderRadius: 4 }} />
      <div className="field-group">
        <div className="field-group-head">
          <div className="skeleton" style={{ height: 10, width: 40, borderRadius: 3 }} />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            <div className="skeleton" style={{ height: 10, width: 90, marginBottom: 8, borderRadius: 3 }} />
            <div className="skeleton" style={{ height: 38, borderRadius: "var(--r-sm)" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: "16px 20px",
        background: "rgba(229,138,109,0.10)",
        border: "1px solid rgba(229,138,109,0.35)",
        borderRadius: "var(--r-md)",
        color: "#e58a6d",
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <Icon name="x" size={14} />
      {message}
    </div>
  );
}
